import {
  Citations,
  OnboardingLayout,
  OnboardingResultsPending,
  OnboardingResultsStatusScreen,
  useSlideSetup,
  useResultsAreInSlide,
  citations,
} from '@/features/client-onboarding';
import { OnboardingOurPredictions, OnboardingCostFactors } from '@/pages';
import { SlideOutSheet, ResultsAreInSlideOutContent } from '@/components';
import { selectSession, selectFeatureFlags } from '@/model';
import { useSelector } from 'react-redux';
import { SessionType } from '@shared';

import advisorImageUrl from '@/images/OnboardingResultsAreIn.png';

export function OnboardingResultsAreIn({ clientId }: ClientIdProps) {
  const { sessionType } = useSelector(selectSession);
  const featureFlags = useSelector(selectFeatureFlags);
  const isD2C = sessionType === SessionType.client && !!featureFlags.d2cMode;

  const {
    clientHasNoAccess,
    clientFirstName,
    clientHasStartedLtc,
    isAlgorithmFinishedRunning,
    hasAlgorithmError,
    retryCountdown,
    handleRefresh,
  } = useResultsAreInSlide(clientId);

  if (clientHasNoAccess && !isD2C) {
    return <OnboardingResultsPending />;
  }

  if (hasAlgorithmError) {
    return (
      <OnboardingResultsStatusScreen
        variant="error"
        onRefresh={handleRefresh}
      />
    );
  }

  if (!isAlgorithmFinishedRunning) {
    return (
      <OnboardingResultsStatusScreen
        variant="pending"
        retryCountdown={retryCountdown}
        onRefresh={handleRefresh}
      />
    );
  }

  return (
    <OnboardingResultsAreInContent
      clientFirstName={clientFirstName}
      clientHasStartedLtc={clientHasStartedLtc}
    />
  );
}

interface OnboardingResultsAreInContentProps {
  clientFirstName: string;
  clientHasStartedLtc: boolean;
}

function OnboardingResultsAreInContent({
  clientFirstName,
  clientHasStartedLtc,
}: OnboardingResultsAreInContentProps) {
  useSlideSetup({
    slidePage: OnboardingResultsAreIn,
    nextButtonLabel: clientHasStartedLtc
      ? `See ${clientFirstName}'s Costs`
      : `See ${clientFirstName}'s Predictions`,
    nextSlidePage: clientHasStartedLtc ? OnboardingCostFactors : OnboardingOurPredictions,
  });

  return (
    <OnboardingLayout>
      <div className="flex h-max flex-col items-center justify-center pb-4">
        <div className="grid grid-cols-1 items-center gap-2 pb-6 pt-0 md:grid-cols-2 md:py-16">
          <img
            src={advisorImageUrl}
            className="md:max-w-100 w-full max-w-48 md:mx-auto"
            alt="Results are in"
          />
          <div className="mb-4 flex flex-col gap-3 leading-normal tracking-wide md:gap-5">
            <h1
              data-testid="results-are-in-header"
              className="text-balance text-2xl font-bold text-gray-950 md:text-4xl"
            >
              {clientFirstName}'s results are in!
            </h1>
            <p
              className="text-sm md:text-xl"
              data-testid="results-are-in-description"
            >
              {clientFirstName}&apos;s long-term care predictions are made using{' '}
              <span className="text-primary">artificial intelligence (AI)</span>.
            </p>
            <p className="text-sm md:text-xl">
              We compare {clientFirstName}&apos;s information with similar data from nearly{' '}
              <span className="text-primary">50,000</span> families.
            </p>
            <p className="text-sm md:text-xl">
              Our dataset includes over <span className="text-primary">500,000,000</span> data
              points to personalize {clientFirstName}&apos;s predictions.
            </p>
            <p className="text-sm md:text-xl">
              <SlideOutSheet
                slideOutContent={<ResultsAreInSlideOutContent clientFirstName={clientFirstName} />}
                title="FAQ"
              >
                <span className="text-primary hover:text-primary-hover w-full text-left font-normal underline">
                  Learn more
                </span>
              </SlideOutSheet>{' '}
              about our data and methodology.
            </p>
          </div>
        </div>
        <div className="mt-auto pt-4">
          <Citations
            className="w-full"
            citations={[citations.OnboardingResultsAreInDisclaimer]}
          />
        </div>
      </div>
    </OnboardingLayout>
  );
}
